import type { AdLightQuery, AdsForMapQuery, GeometryInput, SearchQuery } from 'src/types/__generated__/graphql';
import type { ExtendedAdsQueryVariables } from '../types/extendedAdsQueryVariables';

export type HoveredAd = AdLightQuery['ad'] | ArrayElement<NonNullable<AdsForMapQuery['ads']>['items']>;

export enum ActionType {
    RESET = 'RESET',
    LOAD_SAVED_SEARCH = 'LOAD_SAVED_SEARCH',
    SET_SELECTED_SAVED_SEARCH = 'SET_SELECTED_SAVED_SEARCH',
    SET_FIT_MAP_ON_AD_SEARCH_STATE_CHANGE = 'SET_FIT_MAP_ON_AD_SEARCH_STATE_CHANGE',
    SET_OPEN_SAVED_SEARCH_TUTORIAL = 'SET_OPEN_SAVED_SEARCH_TUTORIAL',
    SET_HOVERED_AD_STATE = 'SET_HOVERED_AD_STATE',
    SET_IS_MAP_FULLSCREEN = 'SET_IS_MAP_FULLSCREEN',
    SET_PLACES = 'SET_PLACES',
    SET_PLACE = 'SET_PLACE',
    SET_IS_POLYGON_DISPLAYED = 'SET_IS_POLYGON_DISPLAYED',
    SET_STORE_PARTIALLY = 'SET_STORE_PARTIALLY',
}

type ResetAction = { type: ActionType.RESET };
type LoadSavedSearchAction = {
    type: ActionType.LOAD_SAVED_SEARCH;
    payload: {
        search: SearchQuery['search'];
        queryVariables?: ExtendedAdsQueryVariables;
        openSavedSearchTutorial?: boolean;
    };
};
type SetFitMapOnAdSearchStateChangeAction = {
    type: ActionType.SET_FIT_MAP_ON_AD_SEARCH_STATE_CHANGE;
    payload: boolean;
};
type SetOpenSavedSearchTutorialAction = {
    type: ActionType.SET_OPEN_SAVED_SEARCH_TUTORIAL;
    payload: boolean;
};
type SetHoveredAdState = {
    type: ActionType.SET_HOVERED_AD_STATE;
    payload: AdSearchStateStore['hoveredAdState'];
};
type SetIsMapFullscreenAction = { type: ActionType.SET_IS_MAP_FULLSCREEN; payload: boolean };
type SetPlacesAction = { type: ActionType.SET_PLACES; payload: GeometryInput[] | undefined };
type SetPlaceAction = {
    type: ActionType.SET_PLACE;
    payload: ExtendedAdsQueryVariables['place'] | undefined;
};
type SetIsPolygonDisplayedAction = {
    type: ActionType.SET_IS_POLYGON_DISPLAYED;
    payload: boolean;
};
type SetStorePartiallyAction = {
    type: ActionType.SET_STORE_PARTIALLY;
    payload: {
        selectedSavedSearch?: SearchQuery['search'] | null;
        fitMapOnAdSearchStateChange?: boolean;
        openSavedSearchTutorial?: boolean;
        hoveredAdState?: AdSearchStateStore['hoveredAdState'] | null;
        isMapFullscreen?: boolean;
        places?: ExtendedAdsQueryVariables['places'] | null;
        place?: ExtendedAdsQueryVariables['place'] | null;
        isPolygonDisplayed?: boolean;
    };
};

type Action =
    | ResetAction
    | LoadSavedSearchAction
    | SetFitMapOnAdSearchStateChangeAction
    | SetOpenSavedSearchTutorialAction
    | SetHoveredAdState
    | SetIsMapFullscreenAction
    | SetPlacesAction
    | SetPlaceAction
    | SetIsPolygonDisplayedAction
    | SetStorePartiallyAction;

export type AdSearchStateStore = {
    selectedSavedSearch: SearchQuery['search'] | undefined;
    fitMapOnAdSearchStateChange: boolean;
    openSavedSearchTutorial: boolean;
    hoveredAdState: {
        hoveredAd?: HoveredAd;
        visibleAd?: HoveredAd;
        hoveredAdData?: HoveredAd;
    };
    isMapFullscreen: boolean;
    places?: GeometryInput[];
    place?: ExtendedAdsQueryVariables['place'];
    isPolygonDisplayed: boolean;
};

export function adSearchStateReducer(state: AdSearchStateStore, action: Action): AdSearchStateStore {
    switch (action.type) {
        case ActionType.RESET: {
            return initialState;
        }
        case ActionType.LOAD_SAVED_SEARCH: {
            return {
                ...initialState,
                selectedSavedSearch: action.payload.search,
                isPolygonDisplayed: true,
                places: action.payload.queryVariables?.places,
                place: action.payload.queryVariables?.place,
                fitMapOnAdSearchStateChange: true,
                ...(action.payload.openSavedSearchTutorial !== undefined && {
                    openSavedSearchTutorial: action.payload.openSavedSearchTutorial,
                }),
            };
        }
        case ActionType.SET_FIT_MAP_ON_AD_SEARCH_STATE_CHANGE: {
            return {
                ...state,
                fitMapOnAdSearchStateChange: action.payload,
            };
        }
        case ActionType.SET_OPEN_SAVED_SEARCH_TUTORIAL: {
            return {
                ...state,
                openSavedSearchTutorial: action.payload,
            };
        }
        case ActionType.SET_HOVERED_AD_STATE: {
            return {
                ...state,
                hoveredAdState: {
                    ...state.hoveredAdState,
                    ...action.payload,
                },
            };
        }
        case ActionType.SET_IS_MAP_FULLSCREEN: {
            return {
                ...state,
                isMapFullscreen: action.payload,
            };
        }
        case ActionType.SET_PLACES: {
            return {
                ...state,
                places: action.payload,
            };
        }
        case ActionType.SET_PLACE: {
            return {
                ...state,
                place: action.payload,
            };
        }
        case ActionType.SET_IS_POLYGON_DISPLAYED: {
            return {
                ...state,
                isPolygonDisplayed: action.payload,
            };
        }
        case ActionType.SET_STORE_PARTIALLY: {
            return {
                ...state,
                ...{
                    ...(action.payload.selectedSavedSearch !== undefined && {
                        selectedSavedSearch: action.payload.selectedSavedSearch ?? undefined,
                    }),
                    ...(action.payload.fitMapOnAdSearchStateChange !== undefined && {
                        fitMapOnAdSearchStateChange: action.payload.fitMapOnAdSearchStateChange,
                    }),
                    ...(action.payload.openSavedSearchTutorial !== undefined && {
                        openSavedSearchTutorial: action.payload.openSavedSearchTutorial,
                    }),
                    ...(action.payload.hoveredAdState !== undefined && {
                        hoveredAdState: action.payload.hoveredAdState ?? undefined,
                    }),
                    ...(action.payload.isMapFullscreen !== undefined && {
                        isMapFullscreen: action.payload.isMapFullscreen,
                    }),
                    ...(action.payload.places !== undefined && {
                        places: action.payload.places ?? undefined,
                    }),
                    ...(action.payload.place !== undefined && {
                        place: action.payload.place ?? undefined,
                    }),
                    ...(action.payload.isPolygonDisplayed !== undefined && {
                        isPolygonDisplayed: action.payload.isPolygonDisplayed,
                    }),
                },
            };
        }
        default:
            return state;
    }
}

export const initialState: AdSearchStateStore = {
    selectedSavedSearch: undefined,
    fitMapOnAdSearchStateChange: true,
    openSavedSearchTutorial: false,
    hoveredAdState: {
        hoveredAd: undefined,
        visibleAd: undefined,
        hoveredAdData: undefined,
    },
    isMapFullscreen: false,
    places: undefined,
    place: undefined,
    isPolygonDisplayed: false,
};
